import { WorkflowType } from '@/constants/dispositions';

/**
 * Last-resort formatter for a payer slug when the payor list doesn't have a
 * matching entry — turns `express_scripts` into `EXPRESS SCRIPTS`.
 */
export function fallbackPayerLabel(slug: string): string {
  return slug.replace(/_/g, ' ').toUpperCase();
}

/**
 * Pick the right input-data field for the case's payer slug:
 *   - PBM workflows store it as `payor_name` (with an O).
 *   - Transportation workflows store it as `payer_name` (with an E) — the
 *     spelling difference is intentional, not a typo.
 *   - All MedBV-class workflows (medbv / unifiedmedbv / others) store it as
 *     `primary_payer_name`, with `payor_name` as a defensive fallback.
 */
export function extractPayerSlug(
  workflowType: string | undefined,
  inputData: Record<string, unknown> | undefined,
): string {
  if (!inputData) return '';
  if (workflowType === WorkflowType.TRANSPORTATION) {
    return typeof inputData.payer_name === 'string' ? inputData.payer_name : '';
  }
  const primary =
    typeof inputData.primary_payer_name === 'string' ? inputData.primary_payer_name : '';
  const legacy = typeof inputData.payor_name === 'string' ? inputData.payor_name : '';
  if (workflowType === WorkflowType.PBM) return legacy;
  return primary || legacy;
}

/**
 * Resolve a case's payer slug to a human-readable label, optionally using the
 * supplied slug → name map (typically built from `usePayors()`). Falls back to
 * a formatted version of the slug when the map is omitted or has no entry.
 */
export function resolvePayerLabel(
  workflowType: string | undefined,
  inputData: Record<string, unknown> | undefined,
  payorNameBySlug?: Map<string, string>,
): string {
  const slug = extractPayerSlug(workflowType, inputData);
  if (!slug) return '';
  return payorNameBySlug?.get(slug) ?? fallbackPayerLabel(slug);
}

/**
 * Input field holding the payor slug the AI call dials, per workflow type.
 * Mirrors the backend's `casePayorFieldByWorkflowType` (service/call_payer_hours.go),
 * itself a mirror of bvagent's WORKFLOW_PAYOR_FIELD_MAP; keep all three in step.
 * It differs from `extractPayerSlug` for the PA Status types (payor_name only).
 */
const DIALER_PAYOR_FIELD_BY_WORKFLOW_TYPE: Readonly<Record<string, string>> = {
  [WorkflowType.PBM]: 'payor_name',
  [WorkflowType.PA_STATUS_PBM]: 'payor_name',
  [WorkflowType.PA_STATUS_MEDBV]: 'payor_name',
  [WorkflowType.MEDBV]: 'primary_payer_name',
  [WorkflowType.UNIFIEDMEDBV]: 'primary_payer_name',
  medbv_generic: 'primary_payer_name',
  [WorkflowType.TRANSPORTATION]: 'payer_name',
};

/**
 * The payor slug whose working hours the case's AI calls wait on (BV-2525):
 * the workflow type's payor field, trimmed; `payor_name` for unlisted types.
 * The backend also lets `case_record_data` override the input field, which the
 * case list response does not carry, so this reads the input data only.
 * Empty when the field is absent or not a string.
 */
export function extractPayerHoursSlug(
  workflowType: string | undefined,
  inputData: Record<string, unknown> | undefined,
): string {
  const field = DIALER_PAYOR_FIELD_BY_WORKFLOW_TYPE[workflowType ?? ''] ?? 'payor_name';
  const value = inputData?.[field];
  return typeof value === 'string' ? value.trim() : '';
}
